import { useChatGptFilter, chatgptParam } from "../lib/useChatGptFilter";
import { PrintCheckPanel } from "../components/design/PrintCheckPanel";
import { ChatGptFilterButton } from "../components/ChatGptFilterButton";
import { useState, useCallback, useRef, useEffect } from "react";
import { Link } from "react-router-dom";
import {
  Upload,
  Search,
  ChevronLeft,
  ChevronRight,
  Trash2,
  Loader2,
  Tag,
  ExternalLink,
  RotateCcw,
  RefreshCw,
  Edit3,
  Check,
  X,
  Eye,
  Link2,
  Filter,
  ImageIcon,
  Palette,
  Sun,
  Moon,
  LayoutGrid,
  Grid3x3,
  LayoutList,
  Download,
} from "lucide-react";
import type { Design, PersonalizationField, Analysis } from "@erc/shared";
import { PersonalizationFieldsEditor } from "../components/analysis/PersonalizationFieldsEditor";
import { OrderDesignModal } from "../components/analysis/OrderDesignModal";
import { apiClient } from "../lib/api";
import env from "../env";
import DesignEditorPanel from "../components/design/DesignEditorPanel";
import { useActiveShopId, useShopStore } from "../store/shop.store";
import { SellingMockupPicker } from "../components/mockup/SellingMockupPicker";
import { DesignStylesTab } from "../components/design/DesignStylesTab";
import { useShopVertical } from "../lib/useShopVertical";
import { authHeaders, getBackendUrl } from "../lib/http";
import { downloadPrintFile } from "../lib/print-file";

const API_BASE = env.API_URL;

const SOURCE_LABELS: Record<string, string> = {
  upload: "Yükleme",
  "mockup-studio": "Mockup Studio",
  "design-builder": "Design Builder",
  canva: "Canva",
};

// Telif durumu rozeti (analizden gelen tasarimlarda)
const COPYRIGHT_STYLE: Record<string, { bg: string; color: string; label: string }> = {
  low: { bg: "#ecfdf5", color: "#047857", label: "Telif: Düşük" },
  medium: { bg: "#fffbeb", color: "#b45309", label: "Telif: Orta" },
  high: { bg: "#fef2f2", color: "#dc2626", label: "Telif: Yüksek" },
};
function CopyrightBadge({ copyright, small }: { copyright?: Design["copyright"]; small?: boolean }) {
  if (!copyright?.level) return null;
  const s = COPYRIGHT_STYLE[copyright.level] ?? COPYRIGHT_STYLE.low;
  return (
    <span
      title={copyright.summary || s.label}
      style={{ background: s.bg, color: s.color }}
      className={`inline-flex items-center rounded font-medium ${small ? "px-1.5 py-0.5 text-[10px]" : "px-2 py-0.5 text-xs"}`}
    >
      {s.label}
    </span>
  );
}

export default function DesignsPage() {
  const [activeTab, setActiveTab] = useState<"pool" | "styles">(() => {
    // ?search= ile gelindiginde her zaman havuz sekmesi acilir
    if (new URLSearchParams(window.location.search).get("search")) return "pool";
    const v = localStorage.getItem("erc_designs_tab");
    return v === "styles" ? "styles" : "pool";
  });
  const changeTab = (t: "pool" | "styles") => { setActiveTab(t); localStorage.setItem("erc_designs_tab", t); };
  const [designs, setDesigns] = useState<Design[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  // ?search=... ile acilabilir (or. Siparisler sayfasindan SKU ile gelis)
  const [search, setSearch] = useState(() => new URLSearchParams(window.location.search).get("search") || "");
  const [sourceFilter, setSourceFilter] = useState("");
  // Kategori filtresi — "" tumu, "v:apparel" dikey, "t:tshirt" tek kategori.
  // Varsayilan: aktif magazanin kategorisi (dikeyi).
  const [categoryFilter, setCategoryFilter] = useState("");
  const [categoryTouched, setCategoryTouched] = useState(false);
  const { isConceptVertical } = useShopVertical();
  const [productTypes, setProductTypes] = useState<Array<{ key: string; label: string; vertical: string }>>([]);
  const shops = useShopStore((s) => s.shops);
  const activeShopId = useActiveShopId();
  const [sortBy, setSortBy] = useState("newest");
  const [copyrightFilter, setCopyrightFilter] = useState("");
  const [noListing, setNoListing] = useState(false);
  const [customOnly, setCustomOnly] = useState(false);
  const [chatgptOnly, setChatgptOnly] = useChatGptFilter();
  const [viewMode, setViewMode] = useState<"buyuk" | "kucuk" | "satir">(() => {
    const v = localStorage.getItem("erc_designs_view");
    return v === "buyuk" || v === "satir" ? v : "kucuk";
  });
  const changeViewMode = (v: "buyuk" | "kucuk" | "satir") => { setViewMode(v); localStorage.setItem("erc_designs_view", v); };
  const [page, setPage] = useState(1);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [uploading, setUploading] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Detail / edit modal
  const [detailDesign, setDetailDesign] = useState<Design | null>(null);
  // ?edit=<id>: baska sayfadan (Liste Denetimi) yeni sekmede duzenlemek icin
  useEffect(() => {
    const id = new URLSearchParams(window.location.search).get("edit");
    if (!id) return;
    apiClient.getDesign(id).then((d) => { setDetailDesign(d); setShowOriginal(false); }).catch(() => { /* bulunamadi */ });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const [showOriginal, setShowOriginal] = useState(false);

  // Inline edit states
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editName, setEditName] = useState("");
  const [editSku, setEditSku] = useState("");

  // Tag modal
  const [tagModalIds, setTagModalIds] = useState<string[]>([]);
  const [tagInput, setTagInput] = useState("");

  // Design editor
  const [editorDesign, setEditorDesign] = useState<Design | null>(null);

  // Ilham alinan analiz secici (kart / detay modalindan acilir)
  const [pickerDesign, setPickerDesign] = useState<Design | null>(null);
  async function handleSetSourceAnalysis(designId: string, analysisId: string | null) {
    await apiClient.setDesignSourceAnalysis(designId, analysisId);
    await fetchDesigns();
    if (detailDesign?.id === designId) {
      const updated = await apiClient.getDesign(designId).catch(() => null);
      if (updated) setDetailDesign(updated);
    }
    setPickerDesign(null);
  }

  // Message
  const [msg, setMsg] = useState("");

  const fetchDesigns = useCallback(async () => {
    setLoading(true);
    try {
      const result = await apiClient.listDesigns({
        search: search || undefined,
        source: sourceFilter || undefined,
        sort: sortBy,
        copyright: copyrightFilter || undefined,
        vertical: categoryFilter.startsWith("v:") ? categoryFilter.slice(2) : undefined,
        productTypeKey: categoryFilter.startsWith("t:") ? categoryFilter.slice(2) : undefined,
        noListing: noListing || undefined,
        custom: customOnly || undefined,
        chatgpt: chatgptParam(chatgptOnly),
        page,
        limit: 24,
      });
      setDesigns(result.data);
      setTotal(result.total);
    } catch (err: any) {
      console.error("Tasarımlar yüklenemedi:", err);
    } finally {
      setLoading(false);
    }
  }, [search, sourceFilter, sortBy, copyrightFilter, categoryFilter, noListing, customOnly, chatgptOnly, page]);

  useEffect(() => {
    fetchDesigns();
  }, [fetchDesigns]);

  // Kategori listesi
  useEffect(() => {
    fetch(`${API_BASE}/api/product-types`, {
      headers: authHeaders(),
    })
      .then((r) => (r.ok ? r.json() : { data: [] }))
      .then((j) => setProductTypes(j.data ?? []))
      .catch(() => { /* kategori listesi alinamazsa filtre "tumu" kalir */ });
  }, []);

  // Varsayilan: aktif magazanin kategorisi (dikeyi). Kullanici degistirdiyse dokunma.
  useEffect(() => {
    if (categoryTouched) return;
    const shop = shops.find((x) => x.id === activeShopId);
    if (shop?.vertical) setCategoryFilter(`v:${shop.vertical}`);
  }, [shops, activeShopId, categoryTouched]);

  // Debounced search
  const searchTimeout = useRef<ReturnType<typeof setTimeout>>();
  function handleSearchChange(val: string) {
    setSearch(val);
    setPage(1);
    clearTimeout(searchTimeout.current);
    searchTimeout.current = setTimeout(() => {
      // fetchDesigns triggered by useEffect
    }, 300);
  }

  // Upload files
  async function handleFiles(files: FileList | File[]) {
    setUploading(true);
    try {
      for (const file of Array.from(files)) {
        await apiClient.uploadDesign(file, { name: file.name.replace(/\.[^.]+$/, "") });
      }
      setMsg(`${files.length} tasarım yüklendi`);
      fetchDesigns();
    } catch (err: any) {
      setMsg("Yükleme hatası: " + err.message);
    } finally {
      setUploading(false);
    }
  }

  // Drag & drop
  function onDragOver(e: React.DragEvent) {
    e.preventDefault();
    setDragOver(true);
  }
  function onDragLeave() {
    setDragOver(false);
  }
  function onDrop(e: React.DragEvent) {
    e.preventDefault();
    setDragOver(false);
    if (e.dataTransfer.files.length) {
      handleFiles(e.dataTransfer.files);
    }
  }

  // Selection
  function toggleSelect(id: string) {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }
  function toggleAll() {
    if (selectedIds.size === designs.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(designs.map((d) => d.id)));
    }
  }

  // Bulk delete
  // -- Toplu Canva / Studio islemleri ------------------------------
  // Her tasarim icin ayri sekme acmak tarayici tarafindan engelleniyor;
  // islem bittikten sonra baglantilar listelenir, kullanici tikladikca acar.
  const [bulkBusy, setBulkBusy] = useState<string | null>(null);
  // Sekmeler arasi bekleme (sn): acilan Canva sekmesinde arka plan silme gibi
  // bir islem yapmaya vakit birakir. Son tercih hatirlanir.
  const [openDelaySec, setOpenDelaySec] = useState<number>(() => {
    const v = Number(localStorage.getItem("erc_canva_open_delay"));
    return Number.isFinite(v) && v >= 0 ? v : 5;
  });
  const [bulkLinks, setBulkLinks] = useState<Array<{ id: string; name: string; url: string }>>([]);
  const [bulkNote, setBulkNote] = useState<string | null>(null);

  const selectedDesigns = () => designs.filter((d) => selectedIds.has(d.id));

  async function handleBulkCanvaOpen() {
    const items = selectedDesigns();
    if (items.length === 0) return;
    // Daha once acilmis olanlar icin TEK bir uyari: tekrar acmak yeni Canva
    // tasarimi olusturur ve eski sekmedeki duzenlemeler alinamaz hale gelir.
    const already = items.filter((d) => ((d.metadata as Record<string, unknown> | null)?.canvaDesignId));
    if (already.length > 0 && !window.confirm(
      `${already.length} tasarım daha önce Canva'da açıldı. Tekrar açmak YENİ Canva tasarımları oluşturur; ` +
      "eski sekmelerdeki düzenlemeler 'Canva'dan güncelle' ile alınamaz. Devam edilsin mi?"
    )) return;
    setBulkLinks([]); setBulkNote(null);

    // KUYRUK: tasarimlar sirayla Canva'ya yuklenir ve her biri hazir olunca
    // KENDI yeni sekmesinde acilir. Mevcut sekme hic degismez.
    //
    // Sekme, yukleme bittikten sonra aciliyor; tarayici bunu kullanici
    // hareketi saymadigi icin pop-up engeli devreye girebilir. Engellenenler
    // asagidaki baglanti listesinde kalir (siteye pop-up izni verilince
    // sonraki seferlerde kendiliginden acilir).
    const token = localStorage.getItem("erc_token") || "";
    const links: Array<{ id: string; name: string; url: string }> = [];
    let failed = 0, blocked = 0;

    for (const [i, d] of items.entries()) {
      setBulkBusy(`Canva'ya yükleniyor ${i + 1}/${items.length}`);
      try {
        const res = await fetch(`${API_BASE}/api/canva/create-design`, {
          method: "POST",
          headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
          body: JSON.stringify({ imageUrl: absUrl(d.editedUrl || d.originalUrl), title: d.name }),
        });
        const json = await res.json();
        if (json.data?.editUrl) {
          links.push({ id: d.id, name: d.name, url: json.data.editUrl });
          setBulkLinks([...links]); // liste anlik dolsun
          if (json.data.designId) await persistCanvaDesignId(d, json.data.designId);
          // noopener: yeni sekme bu sayfayi yonlendiremesin
          const w = window.open(json.data.editUrl, "_blank", "noopener");
          if (!w) blocked++;
        } else {
          failed++;
        }
      } catch {
        failed++;
      }

      // Sonraki sekmeden once bekle: acilan sekmede islem yapmaya (arka plani
      // kaldirmak gibi) vakit kalsin. Geri sayim ilerleme yazisinda gorunur.
      if (openDelaySec > 0 && i < items.length - 1) {
        for (let left = openDelaySec; left > 0; left--) {
          setBulkBusy(`Sonraki sekme ${left} sn sonra (${i + 1}/${items.length})`);
          await new Promise((r) => setTimeout(r, 1000));
        }
      }
    }
    setBulkBusy(null);
    setBulkLinks(links);
    setBulkNote(
      links.length === 0
        ? "Canva'da açılamadı - bağlantı yok olabilir (Ayarlar > Canva)."
        : `${links.length} tasarım Canva'ya yüklendi${failed ? ` - ${failed} başarısız` : ""}.` +
          (blocked
            ? ` ${blocked} sekme tarayıcı tarafından engellendi - adres çubuğundaki pop-up simgesinden izin ver ya da aşağıdan aç.`
            : "")
    );
    // Kimlikler metadata'ya yazildi: "Canva'dan guncelle" calisabilsin
    await fetchDesigns();
  }

  async function handleBulkCanvaSync() {
    const items = selectedDesigns().filter(
      (d) => ((d.metadata as Record<string, unknown> | null)?.canvaDesignId as string) ?? null
    );
    setBulkLinks([]);
    if (items.length === 0) {
      setBulkNote("Seçilenlerin hiçbiri daha önce Canva'da açılmamış.");
      return;
    }
    const token = localStorage.getItem("erc_token") || "";
    let ok = 0, failed = 0, done = 0;

    // Her tasarim icin Canva'da dis aktarma isi baslatilip bekleniyor; sirayla
    // yapinca 10 tasarim dakikalar suruyordu. Ayni anda birkac tanesi islenir
    // (Canva'yi bogmamak icin sinirli).
    const CONCURRENCY = 4;
    let cursor = 0;
    const worker = async () => {
      while (cursor < items.length) {
        const d = items[cursor++];
        const canvaId = (d.metadata as Record<string, unknown>).canvaDesignId as string;
        try {
          const exp = await fetch(`${API_BASE}/api/canva/export-design`, {
            method: "POST",
            headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
            body: JSON.stringify({ designId: canvaId }),
          });
          const url = (await exp.json()).data?.urls?.[0];
          if (!url) { failed++; continue; }
          const upd = await fetch(`${API_BASE}/api/designs/${d.id}/edited-from-url`, {
            method: "POST",
            headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
            body: JSON.stringify({ url }),
          });
          if (upd.ok) ok++; else failed++;
        } catch { failed++; }
        finally {
          done++;
          setBulkBusy(`Canva'dan alınıyor ${done}/${items.length}`);
        }
      }
    };
    setBulkBusy(`Canva'dan alınıyor 0/${items.length}`);
    await Promise.all(Array.from({ length: Math.min(CONCURRENCY, items.length) }, worker));
    setBulkBusy(null);
    const skipped = selectedIds.size - items.length;
    setBulkNote(
      `${ok} tasarım Canva'dan güncellendi` +
      (failed ? ` - ${failed} başarısız` : "") +
      (skipped ? ` - ${skipped} tanesi Canva'da açılmamış` : "")
    );
    await fetchDesigns();
  }

  function handleBulkStudioOpen() {
    const items = selectedDesigns();
    if (items.length === 0) return;
    const links = items.map((d) => ({ id: d.id, name: d.name, url: buildMockupStudioUrl(d) }));
    // Studio tek tasarimla calisir: her tasarim ayri sekmede acilir.
    let blocked = 0;
    for (const l of links) {
      if (!window.open(l.url, "_blank")) blocked++;
    }
    setBulkLinks(links);
    setBulkNote(
      blocked
        ? `${links.length - blocked} sekme açıldı - ${blocked} tanesi engellendi, aşağıdan açabilirsin.`
        : `${links.length} tasarım Mockup Studio'da açıldı.`
    );
  }

  async function handleBulkDelete() {
    if (!confirm(`${selectedIds.size} tasarımı silmek istediğinize emin misiniz?`)) return;
    try {
      await apiClient.bulkDeleteDesigns([...selectedIds]);
      setSelectedIds(new Set());
      fetchDesigns();
      setMsg("Seçili tasarımlar silindi");
    } catch (err: any) {
      setMsg("Silme hatası: " + err.message);
    }
  }

  // Bulk tag
  async function handleBulkTag() {
    if (!tagInput.trim()) return;
    const tags = tagInput.split(",").map((t) => t.trim()).filter(Boolean);
    try {
      await apiClient.bulkTagDesigns(tagModalIds, tags);
      setTagModalIds([]);
      setTagInput("");
      fetchDesigns();
      setMsg("Etiketler eklendi");
    } catch (err: any) {
      setMsg("Etiket hatası: " + err.message);
    }
  }

  // Single delete
  async function handleDelete(id: string) {
    if (!confirm("Bu tasarımı silmek istediğinize emin misiniz?")) return;
    try {
      await apiClient.deleteDesign(id);
      fetchDesigns();
      if (detailDesign?.id === id) setDetailDesign(null);
      setMsg("Tasarım silindi");
    } catch (err: any) {
      setMsg("Silme hatası: " + err.message);
    }
  }

  // Inline name/sku edit
  async function saveInlineEdit(id: string) {
    try {
      await apiClient.updateDesign(id, { name: editName, sku: editSku || undefined });
      setEditingId(null);
      fetchDesigns();
    } catch (err: any) {
      setMsg("Güncelleme hatası: " + err.message);
    }
  }

  // Revert
  async function handleRevert(id: string) {
    try {
      await apiClient.revertDesign(id);
      fetchDesigns();
      setMsg("Orijinale döndürüldü");
    } catch (err: any) {
      setMsg("Revert hatası: " + err.message);
    }
  }

  // Editor save handler
  async function handleEditorSave(dataUrl: string) {
    if (!editorDesign) return;
    try {
      const res = await fetch(dataUrl);
      const blob = await res.blob();
      const file = new File([blob], `edited-${Date.now()}.png`, { type: "image/png" });
      await apiClient.uploadEditedDesign(editorDesign.id, file);
      setEditorDesign(null);
      fetchDesigns();
      setMsg("Düzenleme kaydedildi");
    } catch (err: any) {
      setMsg("Düzenleme kaydedilemedi: " + err.message);
    }
  }

  function getEditorUrl(design: Design): string {
    const url = design.editedUrl || design.originalUrl;
    return url.startsWith("http") ? url : `${API_BASE}${url}`;
  }

  const totalPages = Math.ceil(total / 24);

  return (
    <div className="space-y-6">
      {/* Sekmeler */}
      <div className="flex gap-1 border-b border-gray-200">
        {([
          { id: "pool" as const, label: "Tasarım Havuzu", icon: ImageIcon },
          { id: "styles" as const, label: "Tasarım Stilleri", icon: Palette },
        ]).map((t) => {
          const Icon = t.icon;
          return (
            <button
              key={t.id}
              onClick={() => changeTab(t.id)}
              className={`flex items-center gap-1.5 px-4 py-2.5 text-sm font-medium border-b-2 -mb-px transition-colors ${
                activeTab === t.id
                  ? "border-indigo-600 text-indigo-600"
                  : "border-transparent text-gray-500 hover:text-gray-700"
              }`}
            >
              <Icon className="w-4 h-4" /> {t.label}
            </button>
          );
        })}
      </div>

      {activeTab === "styles" && <DesignStylesTab />}

      {/* Taki magazasinda tasarim havuzu kullanilmaz: urun tedarikciden hazir
          gelir, gorsel uretimi konsept fotograf uzerinden yurur. Havuzu
          gizlemek yerine NEDENINI yaziyoruz — bos liste "veri kayboldu" gibi
          okunuyordu. */}
      {activeTab === "pool" && isConceptVertical && (
        <div className="rounded-xl border border-amber-200 bg-amber-50 p-5">
          <h2 className="text-sm font-semibold text-amber-900">Takı mağazası seçili</h2>
          <p className="mt-1 text-sm text-amber-800">
            Takıda tasarım havuzu kullanılmaz. Ürün, Tedarikçi Katalog'daki SKU'dan gelir;
            görseller konsept fotoğrafları üzerinden üretilir.
          </p>
          <p className="mt-2 text-xs text-amber-700">
            Tekstil tasarımlarını görmek için üst bardan tekstil mağazasına geçin.
          </p>
        </div>
      )}

      {activeTab === "pool" && !isConceptVertical && (
      <>
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Tasarım Havuzu</h1>
          <p className="text-sm text-gray-500 mt-1">{total} tasarım</p>
        </div>
        <button
          onClick={() => fileInputRef.current?.click()}
          disabled={uploading}
          className="flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50"
        >
          {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
          Tasarım Yükle
        </button>
        <input
          ref={fileInputRef}
          type="file"
          accept="image/png,image/jpeg,image/webp,image/svg+xml"
          multiple
          className="hidden"
          onChange={(e) => e.target.files && handleFiles(e.target.files)}
        />
      </div>

      {/* Message */}
      {msg && (
        <div className="p-3 bg-blue-50 text-blue-700 rounded-lg flex items-center justify-between">
          <span className="text-sm">{msg}</span>
          <button onClick={() => setMsg("")}><X className="w-4 h-4" /></button>
        </div>
      )}

      {/* Drag & drop zone */}
      <div
        onDragOver={onDragOver}
        onDragLeave={onDragLeave}
        onDrop={onDrop}
        className={`border-2 border-dashed rounded-xl p-6 text-center transition-colors ${
          dragOver ? "border-indigo-500 bg-indigo-50" : "border-gray-300 hover:border-gray-400"
        }`}
      >
        <ImageIcon className="w-8 h-8 mx-auto text-gray-400 mb-2" />
        <p className="text-sm text-gray-500">
          Tasarımları buraya sürükleyip bırakın veya{" "}
          <button onClick={() => fileInputRef.current?.click()} className="text-indigo-600 underline">
            dosya seçin
          </button>
        </p>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-3 items-center">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input
            type="text"
            placeholder="Tasarım ara (isim, SKU)..."
            value={search}
            onChange={(e) => handleSearchChange(e.target.value)}
            className="w-full pl-10 pr-3 py-2 border rounded-lg text-sm"
          />
        </div>

        {/* Kategori filtresi — varsayilan aktif magazanin kategorisi */}
        <select
          value={categoryFilter}
          onChange={(e) => { setCategoryFilter(e.target.value); setCategoryTouched(true); setPage(1); }}
          title="Tasarımın bağlı olduğu ilham listesinin kategorisine göre filtrele"
          className="rounded-lg border px-3 py-2 text-sm"
        >
          <option value="">Tüm Kategoriler</option>
          {[...new Set(productTypes.map((p) => p.vertical))].map((v) => (
            <option key={`v:${v}`} value={`v:${v}`}>
              {v === "apparel" ? "Tekstil" : v === "giftware" ? "Hediyelik" : v === "jewelry" ? "Takı" : v} (tümü)
            </option>
          ))}
          {productTypes.map((p) => (
            <option key={`t:${p.key}`} value={`t:${p.key}`}>{p.label}</option>
          ))}
        </select>

        <div className="flex items-center gap-1">
          <Filter className="w-4 h-4 text-gray-400" />
          <select
            value={sourceFilter}
            onChange={(e) => { setSourceFilter(e.target.value); setPage(1); }}
            className="border rounded-lg px-3 py-2 text-sm"
          >
            <option value="">Tüm Kaynaklar</option>
            <option value="upload">Yükleme</option>
            <option value="mockup-studio">Mockup Studio</option>
            <option value="design-builder">Design Builder</option>
            <option value="canva">Canva</option>
          </select>
        </div>

        <select
          value={copyrightFilter}
          onChange={(e) => { setCopyrightFilter(e.target.value); setPage(1); }}
          className="border rounded-lg px-3 py-2 text-sm"
        >
          <option value="">Tüm telif</option>
          <option value="low">Telif: Düşük</option>
          <option value="medium">Telif: Orta</option>
          <option value="high">Telif: Yüksek</option>
        </select>

        <button
          onClick={() => { setNoListing((v) => !v); setPage(1); }}
          title="Sadece hiç listing'e bağlanmamış tasarımlar"
          className={`rounded-lg border px-3 py-2 text-sm font-medium transition-colors ${noListing ? "border-indigo-500 bg-indigo-50 text-indigo-700" : "text-gray-600 hover:bg-gray-50"}`}
        >
          Listesi olmayanlar
        </button>
        <button
          onClick={() => { setCustomOnly((v) => !v); setPage(1); }}
          title="Sadece Custom (kişiselleştirilebilir) tasarımlar"
          className={`rounded-lg border px-3 py-2 text-sm font-medium transition-colors ${customOnly ? "border-pink-500 bg-pink-50 text-pink-700" : "text-gray-600 hover:bg-gray-50"}`}
        >
          Custom
        </button>
        <ChatGptFilterButton value={chatgptOnly} onChange={(v) => { setChatgptOnly(v); setPage(1); }} />

        <select
          value={sortBy}
          onChange={(e) => { setSortBy(e.target.value); setPage(1); }}
          className="border rounded-lg px-3 py-2 text-sm"
        >
          <option value="newest">En Yeni</option>
          <option value="oldest">En Eski</option>
          <option value="name">İsme Göre</option>
          <option value="sku">SKU'ya Göre</option>
        </select>

        {/* Gorunum modu */}
        <div className="flex items-center rounded-lg border p-0.5">
          <button onClick={() => changeViewMode("buyuk")} title="Büyük"
            className={`p-1.5 rounded ${viewMode === "buyuk" ? "bg-indigo-100 text-indigo-600" : "text-gray-400 hover:text-gray-600"}`}>
            <LayoutGrid className="w-4 h-4" />
          </button>
          <button onClick={() => changeViewMode("kucuk")} title="Küçük"
            className={`p-1.5 rounded ${viewMode === "kucuk" ? "bg-indigo-100 text-indigo-600" : "text-gray-400 hover:text-gray-600"}`}>
            <Grid3x3 className="w-4 h-4" />
          </button>
          <button onClick={() => changeViewMode("satir")} title="Satır"
            className={`p-1.5 rounded ${viewMode === "satir" ? "bg-indigo-100 text-indigo-600" : "text-gray-400 hover:text-gray-600"}`}>
            <LayoutList className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* Bulk actions */}
      {selectedIds.size > 0 && (
        <div className="flex items-center gap-3 p-3 bg-indigo-50 rounded-lg">
          <span className="text-sm font-medium text-indigo-700">{selectedIds.size} seçili</span>
          <button
            onClick={handleBulkDelete}
            className="flex items-center gap-1 px-3 py-1.5 bg-red-600 text-white rounded text-sm hover:bg-red-700"
          >
            <Trash2 className="w-3.5 h-3.5" /> Sil
          </button>
          <button
            onClick={() => setTagModalIds([...selectedIds])}
            className="flex items-center gap-1 px-3 py-1.5 bg-gray-600 text-white rounded text-sm hover:bg-gray-700"
          >
            <Tag className="w-3.5 h-3.5" /> Etiket Ekle
          </button>
          <div className="flex items-center gap-1">
            <button
              onClick={() => void handleBulkCanvaOpen()}
              disabled={!!bulkBusy}
              className="flex items-center gap-1 px-3 py-1.5 bg-cyan-600 text-white rounded text-sm hover:bg-cyan-700 disabled:opacity-50"
            >
              <ExternalLink className="w-3.5 h-3.5" /> Canva&apos;da Aç
            </button>
            {/* Sekmeler arasi bekleme: acilan sekmede islem yapmaya vakit birakir */}
            <label className="flex items-center gap-1 rounded border border-cyan-200 bg-white px-1.5 py-1 text-[11px] text-gray-600"
              title="Sekmeler arası bekleme (saniye) - açılan Canva sekmesinde işlem yapmak için">
              <input
                type="number"
                min={0}
                max={120}
                value={openDelaySec}
                onChange={(e) => {
                  const v = Math.max(0, Math.min(120, Number(e.target.value) || 0));
                  setOpenDelaySec(v);
                  try { localStorage.setItem("erc_canva_open_delay", String(v)); } catch { /* ignore */ }
                }}
                className="w-10 rounded border border-gray-200 px-1 py-0.5 text-[11px]"
              />
              sn ara
            </label>
          </div>
          <button
            onClick={() => void handleBulkCanvaSync()}
            disabled={!!bulkBusy}
            className="flex items-center gap-1 px-3 py-1.5 bg-cyan-50 text-cyan-700 border border-cyan-200 rounded text-sm hover:bg-cyan-100 disabled:opacity-50"
          >
            <RefreshCw className="w-3.5 h-3.5" /> Canva&apos;dan Güncelle
          </button>
          <button
            onClick={handleBulkStudioOpen}
            disabled={!!bulkBusy}
            className="flex items-center gap-1 px-3 py-1.5 bg-teal-600 text-white rounded text-sm hover:bg-teal-700 disabled:opacity-50"
          >
            <ImageIcon className="w-3.5 h-3.5" /> Mockup Studio
          </button>
          {bulkBusy && (
            <span className="flex items-center gap-1 text-xs text-indigo-700">
              <Loader2 className="h-3.5 w-3.5 animate-spin" /> {bulkBusy}
            </span>
          )}
          <button
            onClick={() => setSelectedIds(new Set())}
            className="text-sm text-gray-500 hover:text-gray-700 ml-auto"
          >
            Seçimi Kaldır
          </button>
        </div>
      )}

      {(bulkNote || bulkLinks.length > 0) && (
        <div className="mt-2 rounded-lg border border-gray-200 bg-white p-3">
          <div className="flex items-start justify-between gap-3">
            <p className="text-xs text-gray-700">{bulkNote}</p>
            <button onClick={() => { setBulkNote(null); setBulkLinks([]); }} className="text-gray-400 hover:text-gray-600">
              <X className="h-3.5 w-3.5" />
            </button>
          </div>
          {bulkLinks.length > 0 && (
            <div className="mt-2 flex flex-wrap gap-1.5">
              {bulkLinks.map((l) => (
                <a key={l.id} href={l.url} target="_blank" rel="noopener noreferrer"
                  className="max-w-[220px] truncate rounded border border-indigo-200 bg-indigo-50 px-2 py-1 text-[11px] text-indigo-700 hover:bg-indigo-100"
                  title={l.name}>
                  {l.name}
                </a>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Grid */}
      {loading ? (
        <div className="flex justify-center py-20">
          <Loader2 className="w-8 h-8 animate-spin text-gray-400" />
        </div>
      ) : designs.length === 0 ? (
        <div className="text-center py-20 text-gray-400">
          <ImageIcon className="w-12 h-12 mx-auto mb-3" />
          <p>Henüz tasarım yok</p>
          <p className="text-sm mt-1">Tasarım yükleyerek başlayın</p>
        </div>
      ) : (
        <>
          {/* Select all */}
          <div className="flex items-center gap-2">
            <input
              type="checkbox"
              checked={selectedIds.size === designs.length && designs.length > 0}
              onChange={toggleAll}
              className="rounded"
            />
            <span className="text-xs text-gray-500">Tümünü seç</span>
          </div>

          <div className={
            viewMode === "satir"
              ? "grid grid-cols-1 gap-2"
              : viewMode === "buyuk"
                ? "grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4"
                : "grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 xl:grid-cols-8 gap-3"
          }>
            {designs.map((design) => (
              <DesignCard
                key={design.id}
                design={design}
                list={viewMode === "satir"}
                selected={selectedIds.has(design.id)}
                onSelect={() => toggleSelect(design.id)}
                onView={() => { setDetailDesign(design); setShowOriginal(false); }}
                onDelete={() => handleDelete(design.id)}
                onRevert={() => handleRevert(design.id)}
                onEditStart={() => {
                  setEditingId(design.id);
                  setEditName(design.name);
                  setEditSku(design.sku || "");
                }}
                onTagClick={() => setTagModalIds([design.id])}
                onOpenEditor={() => setEditorDesign(design)}
                onLinkAnalysis={() => setPickerDesign(design)}
                editing={editingId === design.id}
                editName={editName}
                editSku={editSku}
                onEditNameChange={setEditName}
                onEditSkuChange={setEditSku}
                onEditSave={() => saveInlineEdit(design.id)}
                onEditCancel={() => setEditingId(null)}
                onRefresh={() => fetchDesigns()}
              />
            ))}
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-center gap-2 pt-4">
              <button
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={page === 1}
                className="px-3 py-1.5 border rounded text-sm disabled:opacity-50"
              >
                Önceki
              </button>
              <span className="text-sm text-gray-600">
                {page} / {totalPages}
              </span>
              <button
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                disabled={page === totalPages}
                className="px-3 py-1.5 border rounded text-sm disabled:opacity-50"
              >
                Sonraki
              </button>
            </div>
          )}
        </>
      )}
      </>
      )}

      {/* Detail Modal */}
      {detailDesign && (
        <DesignDetailModal
          design={detailDesign}
          showOriginal={showOriginal}
          onToggleOriginal={() => setShowOriginal(!showOriginal)}
          onClose={() => setDetailDesign(null)}
          onDelete={() => handleDelete(detailDesign.id)}
          onRevert={() => { handleRevert(detailDesign.id); setDetailDesign(null); }}
          onOpenEditor={() => { setDetailDesign(null); setEditorDesign(detailDesign); }}
          onLinkAnalysis={() => setPickerDesign(detailDesign)}
          onRemoveAnalysis={() => handleSetSourceAnalysis(detailDesign.id, null)}
          onSellingMockupChange={() => { fetchDesigns(); }}
          onUpdate={async (payload) => {
            await apiClient.updateDesign(detailDesign.id, payload);
            fetchDesigns();
            const updated = await apiClient.getDesign(detailDesign.id);
            setDetailDesign(updated);
          }}
          onRefresh={async () => {
            fetchDesigns();
            const updated = await apiClient.getDesign(detailDesign.id);
            setDetailDesign(updated);
          }}
        />
      )}

      {/* Tag Modal */}
      {tagModalIds.length > 0 && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl shadow-xl p-6 w-full max-w-md">
            <h3 className="text-lg font-semibold mb-4">Etiket Ekle</h3>
            <p className="text-sm text-gray-500 mb-3">{tagModalIds.length} tasarıma etiket ekle (virgül ile ayırın)</p>
            <input
              type="text"
              value={tagInput}
              onChange={(e) => setTagInput(e.target.value)}
              placeholder="örnek: tshirt, vintage, summer"
              className="w-full border rounded-lg px-3 py-2 text-sm mb-4"
              onKeyDown={(e) => e.key === "Enter" && handleBulkTag()}
            />
            <div className="flex justify-end gap-2">
              <button onClick={() => { setTagModalIds([]); setTagInput(""); }} className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800">
                İptal
              </button>
              <button onClick={handleBulkTag} className="px-4 py-2 bg-indigo-600 text-white rounded-lg text-sm hover:bg-indigo-700">
                Ekle
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Design Editor */}
      {editorDesign && (
        <DesignEditorPanel
          designUrl={getEditorUrl(editorDesign)}
          onClose={() => setEditorDesign(null)}
          onSave={handleEditorSave}
        />
      )}

      {/* Ilham alinan analiz secici */}
      {pickerDesign && (
        <AnalysisPickerModal
          currentId={(pickerDesign.metadata as Record<string, unknown> | null)?.analysisId as string | undefined}
          onClose={() => setPickerDesign(null)}
          onPick={(analysisId) => handleSetSourceAnalysis(pickerDesign.id, analysisId)}
        />
      )}
    </div>
  );
}

/** Canva design kimligini tasarimin metadata'sina yazar.
 *
 *  Kimlik yalnizca bilesen state'inde tutuluyordu: sayfa yenilenince ya da
 *  baska bir ekrandan bakildiginda "Canva'dan guncelle" calismiyor,
 *  "hicbiri Canva'da acilmamis" deniyordu. PATCH metadata'yi KOMPLE
 *  degistirdigi icin mevcut alanlarla birlestirilerek gonderilir.
 */
async function persistCanvaDesignId(design: Design, canvaDesignId: string): Promise<void> {
  const meta = { ...((design.metadata ?? {}) as Record<string, unknown>), canvaDesignId };
  try {
    await apiClient.updateDesign(design.id, { metadata: meta });
  } catch { /* kayit basarisizsa Canva sekmesi yine acilir */ }
}

/** Tasarimi Mockup Studio'da acan baglanti. Acik/koyu varyant metadata'dan
 *  cozulur ki studio dogru slotu doldursun. */
export function buildMockupStudioUrl(design: Design): string {
  const meta = (design.metadata ?? {}) as Record<string, unknown>;
  const mainUrl = design.editedUrl || design.originalUrl;
  const theme = meta.theme as "light" | "dark" | undefined;
  const variant = meta.variantUrl as string | undefined;
  const variantTheme = meta.variantTheme as string | undefined;
  let lightUrl: string | undefined;
  let darkUrl: string | undefined;
  if (theme === "dark") { darkUrl = mainUrl; if (variant) lightUrl = variant; }
  else if (theme === "light") { lightUrl = mainUrl; if (variant) darkUrl = variant; }
  else if (variant && variantTheme === "light") { darkUrl = mainUrl; lightUrl = variant; }
  else if (variant && variantTheme === "dark") { lightUrl = mainUrl; darkUrl = variant; }
  else { lightUrl = mainUrl; }
  const p = new URLSearchParams();
  if (lightUrl) p.set("lightUrl", lightUrl);
  if (darkUrl) p.set("darkUrl", darkUrl);
  p.set("designUrl", mainUrl);
  p.set("designId", design.id);
  if (design.sku) p.set("designSku", design.sku);
  if (meta.analysisId) p.set("analysisId", String(meta.analysisId));
  return `/mockup-studio?${p.toString()}`;
}

type PreviewBg = "check" | "tint" | "dark" | "white";

const PREVIEW_BG_CLASS: Record<PreviewBg, string> = {
  check: "bg-alpha-check",
  tint: "bg-alpha-tint",
  dark: "bg-alpha-dark",
  white: "bg-white",
};

const PREVIEW_BG_LABEL: Record<PreviewBg, string> = {
  check: "Damalı (şeffaflık)",
  tint: "Bej zemin",
  dark: "Koyu zemin",
  white: "Beyaz zemin",
};

const absUrl = (u?: string) => (!u ? "" : u.startsWith("http") ? u : `${API_BASE}${u}`);

// Canva islemleri tek yerde: hem karttan hem ayrinti modalindan ayni akis.
// Canva design kimligi tasarimin metadata'sinda tutulur; "guncelle" ancak
// daha once Canva'da acilmis tasarimlarda anlamli.
function useCanvaActions(design: Design, onRefresh: () => void | Promise<void>) {
  const metaId = ((design.metadata as Record<string, unknown> | null)?.canvaDesignId as string) ?? null;
  const [canvaDesignId, setCanvaDesignId] = useState<string | null>(metaId);
  const [opening, setOpening] = useState(false);
  const [syncing, setSyncing] = useState(false);

  useEffect(() => { setCanvaDesignId(metaId); }, [metaId]);

  async function openInCanva() {
    // Ikinci kez acmak Canva'da YENI bir tasarim olusturur: onceki sekmedeki
    // duzenlemeler baglanti disi kalir. Kullanici bunu bilerek secsin.
    if (canvaDesignId && !window.confirm(
      "Bu tasarım daha önce Canva'da açıldı. Tekrar açmak YENİ bir Canva tasarımı oluşturur; " +
      "önceki sekmede yaptığın düzenlemeler artık 'Canva'dan güncelle' ile alınamaz. Devam edilsin mi?"
    )) return;
    setOpening(true);
    try {
      const token = localStorage.getItem("erc_token") || "";
      const url = absUrl(design.editedUrl || design.originalUrl);
      const res = await fetch(`${API_BASE}/api/canva/create-design`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ imageUrl: url, title: design.name }),
      });
      const json = await res.json();
      if (json.data?.editUrl) {
        if (json.data.designId) {
          setCanvaDesignId(json.data.designId);
          await persistCanvaDesignId(design, json.data.designId);
          await onRefresh();
        }
        window.open(json.data.editUrl, "_blank");
      } else {
        alert("Canva bağlantısı yok. Ayarlar > Canva'dan bağlan.");
      }
    } catch {
      alert("Canva'da açılamadı.");
    } finally {
      setOpening(false);
    }
  }

  async function syncFromCanva() {
    if (!canvaDesignId) return;
    setSyncing(true);
    try {
      const token = localStorage.getItem("erc_token") || "";
      const exp = await fetch(`${API_BASE}/api/canva/export-design`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ designId: canvaDesignId }),
      });
      const expJson = await exp.json();
      const url = expJson.data?.urls?.[0];
      if (!url) { alert("Canva'dan güncel görsel alınamadı."); return; }
      const upd = await fetch(`${API_BASE}/api/designs/${design.id}/edited-from-url`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ url }),
      });
      if (!upd.ok) { alert("Tasarım güncellenemedi."); return; }
      await onRefresh();
    } catch {
      alert("Güncellenemedi.");
    } finally {
      setSyncing(false);
    }
  }

  return { canvaDesignId, openInCanva, syncFromCanva, opening, syncing };
}

// ─── Design Card ─────────────────────────────

function DesignCard({
  design,
  list,
  selected,
  onSelect,
  onView,
  onDelete,
  onRevert,
  onEditStart,
  onTagClick,
  onOpenEditor,
  onLinkAnalysis,
  editing,
  editName,
  editSku,
  onEditNameChange,
  onEditSkuChange,
  onEditSave,
  onEditCancel,
  onRefresh,
}: {
  design: Design;
  list?: boolean;
  selected: boolean;
  onSelect: () => void;
  onView: () => void;
  onDelete: () => void;
  onRevert: () => void;
  onEditStart: () => void;
  onTagClick: () => void;
  onOpenEditor: () => void;
  onLinkAnalysis: () => void;
  editing: boolean;
  editName: string;
  editSku: string;
  onEditNameChange: (v: string) => void;
  onEditSkuChange: (v: string) => void;
  onEditSave: () => void;
  onEditCancel: () => void;
  onRefresh: () => void | Promise<void>;
}) {
  const canva = useCanvaActions(design, onRefresh);
  // Ilham analizi onizlemesi: dugmenin ekrandaki konumu (sabit katman icin)
  const [analysisHover, setAnalysisHover] = useState<{ x: number; y: number } | null>(null);
  const imgUrl = (design.editedUrl || design.originalUrl).startsWith("http")
    ? design.editedUrl || design.originalUrl
    : `${API_BASE}${design.editedUrl || design.originalUrl}`;

  const mockupUrl = buildMockupStudioUrl(design);
  // Tasarima bagli listenin basligi (varsa) — "tasarima ait baslik"
  const listingTitle = design.listings?.find((l) => l.listingDraft?.title)?.listingDraft?.title;

  // Ortak aksiyon butonlari (hem grid hem satir gorunumunde kullanilir)
  const actionButtons = (
    <>
      <Link to={mockupUrl} target="_blank" rel="noopener noreferrer" onClick={(e) => e.stopPropagation()} className="p-1.5 bg-white border rounded shadow-sm hover:bg-teal-50" title="Mockup Studio'da Aç (yeni sekme)">
        <ImageIcon className="w-3.5 h-3.5 text-teal-600" />
      </Link>
      <button onClick={onOpenEditor} className="p-1.5 bg-white border rounded shadow-sm hover:bg-violet-50" title="Editörde Düzenle">
        <Palette className="w-3.5 h-3.5 text-violet-600" />
      </button>
      <button onClick={(e) => { e.stopPropagation(); void canva.openInCanva(); }} disabled={canva.opening}
        className="p-1.5 bg-white border rounded shadow-sm hover:bg-cyan-50 disabled:opacity-50" title="Canva'da Aç">
        {canva.opening ? <Loader2 className="w-3.5 h-3.5 animate-spin text-cyan-600" /> : <ExternalLink className="w-3.5 h-3.5 text-cyan-600" />}
      </button>
      {canva.canvaDesignId && (
        <button onClick={(e) => { e.stopPropagation(); void canva.syncFromCanva(); }} disabled={canva.syncing}
          className="p-1.5 bg-white border rounded shadow-sm hover:bg-cyan-50 disabled:opacity-50" title="Canva'dan Güncelle">
          {canva.syncing ? <Loader2 className="w-3.5 h-3.5 animate-spin text-cyan-600" /> : <RefreshCw className="w-3.5 h-3.5 text-cyan-600" />}
        </button>
      )}
      <button onClick={onEditStart} className="p-1.5 bg-white border rounded shadow-sm hover:bg-gray-50" title="Düzenle">
        <Edit3 className="w-3.5 h-3.5 text-gray-600" />
      </button>
      <button onClick={onTagClick} className="p-1.5 bg-white border rounded shadow-sm hover:bg-gray-50" title="Etiket">
        <Tag className="w-3.5 h-3.5 text-gray-600" />
      </button>
      {/* Ilham analizi — uzerine gelince kaynak listenin fotografi gorunur.
          Onizleme SABIT konumlu: kart "overflow-hidden" oldugu ve dugmeler
          kartin en altinda durdugu icin kart icine cizilen kutu kirpiliyordu. */}
      <button
        onClick={(e) => { e.stopPropagation(); onLinkAnalysis(); }}
        onMouseEnter={(e) => {
          if (!design.sourceAnalysis?.imageUrl) return;
          const r = (e.currentTarget as HTMLElement).getBoundingClientRect();
          setAnalysisHover({ x: r.right, y: r.top });
        }}
        onMouseLeave={() => setAnalysisHover(null)}
        className={`p-1.5 bg-white border rounded shadow-sm hover:bg-indigo-50 ${design.sourceAnalysis ? "" : "border-dashed"}`}
        title={design.sourceAnalysis ? "İlham analizini değiştir" : "İlham analizi bağla"}>
        <Link2 className={`w-3.5 h-3.5 ${design.sourceAnalysis ? "text-indigo-600" : "text-gray-400"}`} />
      </button>
      {design.editedUrl && (
        <button onClick={onRevert} className="p-1.5 bg-white border rounded shadow-sm hover:bg-gray-50" title="Orijinale dön">
          <RotateCcw className="w-3.5 h-3.5 text-amber-600" />
        </button>
      )}
      <button onClick={onDelete} className="p-1.5 bg-white border rounded shadow-sm hover:bg-red-50" title="Sil">
        <Trash2 className="w-3.5 h-3.5 text-red-500" />
      </button>

      {analysisHover && design.sourceAnalysis?.imageUrl && (() => {
        const W = 300;
        const left = Math.min(Math.max(8, analysisHover.x - W), window.innerWidth - W - 8);
        const top = Math.max(8, Math.min(analysisHover.y - 320, window.innerHeight - 340));
        return (
          <div
            style={{ position: "fixed", left, top, width: W, zIndex: 60 }}
            className="pointer-events-none overflow-hidden rounded-lg border border-gray-200 bg-white p-1 shadow-2xl"
          >
            <img src={design.sourceAnalysis.imageUrl} alt="" loading="lazy" className="h-auto w-full rounded object-contain" />
            {design.sourceAnalysis.title && (
              <p className="mt-1 line-clamp-2 px-0.5 pb-0.5 text-[10px] leading-snug text-gray-600">
                {design.sourceAnalysis.title}
              </p>
            )}
          </div>
        );
      })()}
    </>
  );

  // ─── Satir (liste) gorunumu ───
  if (list) {
    return (
      <div className={`flex items-center gap-3 rounded-xl border bg-white p-2 hover:shadow-sm transition-shadow ${selected ? "ring-2 ring-indigo-500" : ""}`}>
        <input type="checkbox" checked={selected} onChange={onSelect} className="rounded flex-shrink-0" />
        <div className="h-14 w-14 flex-shrink-0 cursor-pointer overflow-hidden rounded-lg bg-alpha-check" onClick={onView}>
          <img src={imgUrl} alt={design.name} className="h-full w-full object-contain" loading="lazy" />
        </div>
        <div className="min-w-0 flex-1">
          {editing ? (
            <div className="flex items-center gap-1">
              <input type="text" value={editName} onChange={(e) => onEditNameChange(e.target.value)} className="border rounded px-2 py-1 text-xs" placeholder="İsim" autoFocus />
              <input type="text" value={editSku} onChange={(e) => onEditSkuChange(e.target.value)} className="w-24 border rounded px-2 py-1 text-xs" placeholder="SKU" />
              <button onClick={onEditSave} className="p-1 text-green-600 hover:bg-green-50 rounded"><Check className="w-3.5 h-3.5" /></button>
              <button onClick={onEditCancel} className="p-1 text-gray-400 hover:bg-gray-50 rounded"><X className="w-3.5 h-3.5" /></button>
            </div>
          ) : (
            <>
              <p className="truncate text-sm font-medium text-gray-900" title={design.name}>{design.name}</p>
              {listingTitle && <p className="truncate text-[11px] text-gray-500" title={listingTitle}>Başlık: {listingTitle}</p>}
              <div className="mt-0.5 flex flex-wrap items-center gap-1">
                {design.sku && <span className="rounded bg-gray-100 px-1.5 py-0.5 text-[10px] font-mono text-gray-600">{design.sku}</span>}
                <span className="rounded bg-indigo-50 px-1.5 py-0.5 text-[10px] text-indigo-600">{SOURCE_LABELS[design.source] || design.source}</span>
                {design.editedUrl && <span className="rounded bg-amber-50 px-1.5 py-0.5 text-[10px] text-amber-600">Düzenlenmiş</span>}
                <CopyrightBadge copyright={design.copyright} small />
                {Boolean((design.metadata as { seo?: { title?: string } } | null)?.seo?.title) && <span className="rounded bg-violet-50 px-1.5 py-0.5 text-[10px] text-violet-700" title={(design.metadata as { seo?: { title?: string } }).seo?.title}>Metin ✓</span>}
                {/* Ilham listesine atanan satan mockup — uretilen listenin ana gorseli */}
                {design.sourceAnalysis?.sellingMockup && (
                  <SellingMockupPicker value={design.sellingMockup ?? design.sourceAnalysis.sellingMockup} readOnly compact />
                )}
              {design.custom && <span style={{ background: "#fce7f3", color: "#be185d", fontSize: 9, fontWeight: 700, padding: "1px 6px", borderRadius: 999 }}>Custom</span>}
                {design.listings && design.listings.length > 0 && (
                  <span className="flex items-center gap-0.5 text-[10px] text-green-600"><Link2 className="w-3 h-3" />{design.listings.length}</span>
                )}
              </div>
            </>
          )}
        </div>
        {!editing && <div className="flex flex-shrink-0 items-center gap-1">{actionButtons}</div>}
      </div>
    );
  }

  return (
    <div className={`group relative border rounded-xl overflow-hidden bg-white hover:shadow-md transition-shadow ${selected ? "ring-2 ring-indigo-500" : ""}`}>
      {/* Checkbox */}
      <div className="absolute top-2 left-2 z-10">
        <input
          type="checkbox"
          checked={selected}
          onChange={onSelect}
          className="rounded"
        />
      </div>

      {/* Edited badge */}
      {design.editedUrl && (
        <div className="absolute top-2 right-2 z-10 px-1.5 py-0.5 bg-amber-500 text-white text-[10px] rounded font-medium">
          Düzenlenmiş
        </div>
      )}

      {/* Diğer tema (varyant) küçük foto — sağ üst */}
      {(() => {
        const m = (design.metadata ?? {}) as Record<string, unknown>;
        const vUrl = m.variantUrl as string | undefined;
        if (!vUrl) return null;
        const src = vUrl.startsWith("http") ? vUrl : `${API_BASE}${vUrl}`;
        return (
          <div className={`absolute z-10 h-10 w-10 overflow-hidden rounded-md border-2 border-white bg-gray-100 shadow ${design.editedUrl ? "top-9 right-2" : "top-2 right-2"}`}
            title={`Diğer varyasyon (${m.variantTheme === "dark" ? "Açık Tshirt" : "Koyu Tshirt"})`}>
            <img src={src} alt="" className="h-full w-full object-contain" loading="lazy" />
          </div>
        );
      })()}

      {/* Image */}
      <div
        className="aspect-square bg-alpha-check cursor-pointer relative overflow-hidden"
        onClick={onView}
      >
        <img
          src={imgUrl}
          alt={design.name}
          className="w-full h-full object-contain"
          loading="lazy"
        />
        {/* Hover overlay */}
        <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors flex items-center justify-center opacity-0 group-hover:opacity-100">
          <Eye className="w-6 h-6 text-white" />
        </div>
      </div>

      {/* Info */}
      <div className="p-2">
        {editing ? (
          <div className="space-y-1">
            <input
              type="text"
              value={editName}
              onChange={(e) => onEditNameChange(e.target.value)}
              className="w-full border rounded px-2 py-1 text-xs"
              placeholder="İsim"
              autoFocus
            />
            <input
              type="text"
              value={editSku}
              onChange={(e) => onEditSkuChange(e.target.value)}
              className="w-full border rounded px-2 py-1 text-xs"
              placeholder="SKU"
            />
            <div className="flex gap-1">
              <button onClick={onEditSave} className="p-1 text-green-600 hover:bg-green-50 rounded">
                <Check className="w-3.5 h-3.5" />
              </button>
              <button onClick={onEditCancel} className="p-1 text-gray-400 hover:bg-gray-50 rounded">
                <X className="w-3.5 h-3.5" />
              </button>
            </div>
          </div>
        ) : (
          <>
            <p className="text-xs font-medium text-gray-900 truncate" title={design.name}>
              {design.name}
            </p>
            {listingTitle && (
              <p className="text-[10px] text-gray-500 truncate" title={listingTitle}>Başlık: {listingTitle}</p>
            )}
            <div className="flex items-center gap-1 mt-0.5">
              {design.sku && (
                <span className="text-[10px] px-1.5 py-0.5 bg-gray-100 text-gray-600 rounded font-mono">
                  {design.sku}
                </span>
              )}
              <span className="text-[10px] px-1.5 py-0.5 bg-indigo-50 text-indigo-600 rounded">
                {SOURCE_LABELS[design.source] || design.source}
              </span>
              <CopyrightBadge copyright={design.copyright} small />
              {Boolean((design.metadata as { seo?: { title?: string } } | null)?.seo?.title) && <span className="rounded bg-violet-50 px-1.5 py-0.5 text-[10px] text-violet-700" title={(design.metadata as { seo?: { title?: string } }).seo?.title}>Metin ✓</span>}
              {design.custom && <span style={{ background: "#fce7f3", color: "#be185d", fontSize: 9, fontWeight: 700, padding: "1px 6px", borderRadius: 999 }}>Custom</span>}
            </div>
            {design.tags.length > 0 && (
              <div className="flex flex-wrap gap-0.5 mt-1">
                {design.tags.slice(0, 3).map((tag) => (
                  <span key={tag} className="text-[9px] px-1 py-0.5 bg-gray-50 text-gray-500 rounded">
                    {tag}
                  </span>
                ))}
                {design.tags.length > 3 && (
                  <span className="text-[9px] text-gray-400">+{design.tags.length - 3}</span>
                )}
              </div>
            )}
            {design.listings && design.listings.length > 0 && (
              <div className="flex items-center gap-1 mt-1">
                <Link2 className="w-3 h-3 text-green-500" />
                <span className="text-[10px] text-green-600">{design.listings.length} listing</span>
              </div>
            )}
          </>
        )}
      </div>

      {/* Actions on hover */}
      {!editing && (
        <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-white via-white/90 to-transparent p-2 pt-6 opacity-0 group-hover:opacity-100 transition-opacity flex flex-wrap justify-end gap-1">
          {actionButtons}
        </div>
      )}
    </div>
  );
}

// ─── Design Detail Modal ─────────────────────────────

/** Tasarim ayrinti/duzenleme modali.
 *
 *  Liste Denetimi de ayni modali kullanir: tasarim duzenleme ve secme TEK
 *  yerde toplansin diye disari acildi. selectMode verilirse secim dugmeleri
 *  ("Sec ve sonrakine gec") gorunur. */
/** Tasarimin KENDI SEO'su (ChatGPT ya da paket): baslik, etiketler, aciklama — salt okunur */
function DesignSeoBlock({ metadata }: { metadata: Record<string, unknown> | null | undefined }) {
  const seo = (metadata?.seo ?? null) as { title?: string; description?: string; tags?: string[] } | null;
  const [open, setOpen] = useState(false);
  const [copied, setCopied] = useState<string | null>(null);
  if (!seo || (!seo.title && !seo.description && !seo.tags?.length)) return null;
  const copy = (k: string, v: string) => {
    void navigator.clipboard?.writeText(v).then(() => { setCopied(k); setTimeout(() => setCopied(null), 1500); }).catch(() => {});
  };
  const tags = seo.tags ?? [];
  const fromChatGPT = Boolean(metadata?.fromChatGPT) || Boolean(metadata?.package);
  const CopyBtn = ({ k, v }: { k: string; v: string }) => (
    <button onClick={() => copy(k, v)} className="rounded px-1.5 py-0.5 text-[10px] text-gray-500 hover:bg-gray-100">
      {copied === k ? "Kopyalandı" : "Kopyala"}
    </button>
  );
  return (
    <div className="space-y-2 border-t p-4 text-xs">
      <div className="flex items-center gap-2">
        <span className="font-semibold text-gray-600">Liste metni</span>
        {fromChatGPT && <span className="rounded bg-violet-50 px-1.5 py-0.5 text-[10px] font-medium text-violet-700">ChatGPT</span>}
        <span className="text-[10px] text-gray-400">taslak kurulurken kullanılır</span>
      </div>
      {seo.title && (
        <div>
          <div className="flex items-center justify-between text-[10px] text-gray-400">
            <span>Başlık ({seo.title.length}/140)</span><CopyBtn k="t" v={seo.title} />
          </div>
          <p className="font-medium text-gray-800">{seo.title}</p>
        </div>
      )}
      {tags.length > 0 && (
        <div>
          <div className="flex items-center justify-between text-[10px] text-gray-400">
            <span>Etiketler ({tags.length}/13)</span><CopyBtn k="g" v={tags.join(", ")} />
          </div>
          <div className="mt-0.5 flex flex-wrap gap-1">
            {tags.map((t) => <span key={t} className="rounded bg-gray-100 px-1.5 py-0.5 text-gray-700">{t}</span>)}
          </div>
        </div>
      )}
      {seo.description && (
        <div>
          <div className="flex items-center justify-between text-[10px] text-gray-400">
            <span>Açıklama</span><CopyBtn k="d" v={seo.description} />
          </div>
          <p className={`whitespace-pre-line text-gray-700 ${open ? "" : "line-clamp-4"}`}>{seo.description}</p>
          <button onClick={() => setOpen((o) => !o)} className="mt-0.5 text-[11px] text-indigo-600 hover:underline">
            {open ? "Kısalt" : "Tamamını göster"}
          </button>
        </div>
      )}
    </div>
  );
}

export function DesignDetailModal({
  design,
  showOriginal,
  onToggleOriginal,
  onClose,
  onDelete,
  onRevert,
  onOpenEditor,
  onLinkAnalysis,
  onRemoveAnalysis,
  onSellingMockupChange,
  onUpdate,
  onRefresh,
  selectMode,
}: {
  design: Design;
  showOriginal: boolean;
  onToggleOriginal: () => void;
  onClose: () => void;
  onDelete: () => void;
  onRevert: () => void;
  onOpenEditor: () => void;
  onLinkAnalysis: () => void;
  onRemoveAnalysis: () => void;
  /** Satan mockup atamasi degisince listeyi tazele */
  onSellingMockupChange: () => void;
  onUpdate: (payload: { name?: string; sku?: string; tags?: string[] }) => Promise<void>;
  onRefresh: () => Promise<void>;
  /** Liste Denetimi: tasarimi secip sonrakine gecmek icin */
  selectMode?: {
    selected: boolean;
    onToggle: () => void;
    onSelectAndNext: () => void;
    hasNext: boolean;
    index: number;
    total: number;
    /** Secmeden gezinme — tasarimlar arasinda ileri/geri */
    onPrev?: () => void;
    onNext?: () => void;
    hasPrev?: boolean;
  };
}) {
  // Tisorte ozgu: kirpilmis 11/16 inc baski dosyasi ve acik/koyu tisort varyasyonu.
  // Hediyelikte (ornament) baski dosyasi kirpilmamis kare tuvalin kendisi.
  const isApparelShop = useShopVertical().vertical === "apparel";
  // Ornament: tasarimi mockup seklinin kendisine (daire/kalp/kare) en buyuk boyutta sigdir
  const [fitShape, setFitShape] = useState<"round" | "heart" | "square">("round");
  const [fitBusy, setFitBusy] = useState(false);
  const [fitErr, setFitErr] = useState<string | null>(null);
  const fitToPrintArea = async () => {
    setFitBusy(true); setFitErr(null);
    try {
      const r = await fetch(`${getBackendUrl()}/api/designs/${design.id}/fit-shape`, {
        method: "POST", headers: authHeaders(), body: JSON.stringify({ shape: fitShape }),
      });
      if (!r.ok) throw new Error((await r.json().catch(() => ({}))).error?.message ?? "Sığdırılamadı");
      await onRefresh();
    } catch (e) {
      setFitErr(e instanceof Error ? e.message : String(e));
    } finally {
      setFitBusy(false);
    }
  };
  // Onizleme: yakinlastirma ve buyutec. Baski dosyasinda kenar temizligi ve
  // beyaz kalinti ancak yakindan goruluyor.
  const [zoom, setZoom] = useState(1);
  const [loupe, setLoupe] = useState<
    { cx: number; cy: number; bgX: number; bgY: number; bgW: number; bgH: number } | null
  >(null);
  const previewRef = useRef<HTMLDivElement>(null);
  const [editMode, setEditMode] = useState(false);
  const [name, setName] = useState(design.name);
  const [sku, setSku] = useState(design.sku || "");
  const [tagsStr, setTagsStr] = useState(design.tags.join(", "));
  const [saving, setSaving] = useState(false);
  const [themeBusy, setThemeBusy] = useState(false);
  const variantInputRef = useRef<HTMLInputElement>(null);
  const replaceInputRef = useRef<HTMLInputElement>(null);
  const [replaceBusy, setReplaceBusy] = useState(false);

  async function onReplaceFile(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0]; e.target.value = "";
    if (!file) return;
    setReplaceBusy(true);
    try {
      await apiClient.uploadEditedDesign(design.id, file);
      await onRefresh();
    } finally {
      setReplaceBusy(false);
    }
  }

  const meta = (design.metadata ?? {}) as Record<string, unknown>;
  const theme = meta.theme as "light" | "dark" | undefined;
  const variantUrl = meta.variantUrl as string | undefined;
  const variantTheme = meta.variantTheme as string | undefined;

  // Mockup Studio linki: temaya gore Acik/Koyu slotlarini otomatik doldur
  const mockupHref = (() => {
    const mainUrl = design.editedUrl || design.originalUrl;
    let lightUrl: string | undefined;
    let darkUrl: string | undefined;
    if (theme === "dark") { darkUrl = mainUrl; if (variantUrl) lightUrl = variantUrl; }
    else if (theme === "light") { lightUrl = mainUrl; if (variantUrl) darkUrl = variantUrl; }
    else if (variantUrl && variantTheme === "light") { darkUrl = mainUrl; lightUrl = variantUrl; }
    else if (variantUrl && variantTheme === "dark") { lightUrl = mainUrl; darkUrl = variantUrl; }
    else { lightUrl = mainUrl; } // tema yoksa varsayilan Acik
    const p = new URLSearchParams();
    if (lightUrl) p.set("lightUrl", lightUrl);
    if (darkUrl) p.set("darkUrl", darkUrl);
    p.set("designUrl", mainUrl); // geriye donuk uyum
    p.set("designId", design.id);
    if (design.sku) p.set("designSku", design.sku);
    if (meta.analysisId) p.set("analysisId", String(meta.analysisId));
    return `/mockup-studio?${p.toString()}`;
  })();

  async function setTheme(t: "light" | "dark") {
    setThemeBusy(true);
    try { await apiClient.setDesignThemeVariant(design.id, { theme: t }); await onRefresh(); } finally { setThemeBusy(false); }
  }
  function onVariantFile(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0]; e.target.value = "";
    if (!file) return;
    setThemeBusy(true);
    const reader = new FileReader();
    reader.onload = async () => {
      try {
        if (typeof reader.result === "string") { await apiClient.setDesignThemeVariant(design.id, { variantImage: reader.result }); await onRefresh(); }
      } finally { setThemeBusy(false); }
    };
    reader.readAsDataURL(file);
  }
  async function removeVariant() {
    setThemeBusy(true);
    try { await apiClient.setDesignThemeVariant(design.id, { removeVariant: true }); await onRefresh(); } finally { setThemeBusy(false); }
  }

  // Canva akisi kartlarla ORTAK (useCanvaActions) — iki ayri kopya
  // birbirinden ayri duserek farkli davranmasin.
  // Onizleme zemini (seffaflik denetimi) — son tercih hatirlanir
  const [previewBg, setPreviewBg] = useState<PreviewBg>(() => {
    try {
      const v = localStorage.getItem("erc:preview-bg") as PreviewBg | null;
      return v && v in PREVIEW_BG_CLASS ? v : "check";
    } catch { return "check"; }
  });
  const canva = useCanvaActions(design, onRefresh);
  const openInCanva = canva.openInCanva;
  const syncFromCanva = canva.syncFromCanva;
  const canvaBusy = canva.opening;
  const syncBusy = canva.syncing;
  const canvaDesignId = canva.canvaDesignId;

  useEffect(() => {
    setName(design.name);
    setSku(design.sku || "");
    setTagsStr(design.tags.join(", "));
    setImgDims(null);
  }, [design]);

  const displayUrl = showOriginal && design.editedUrl
    ? (design.originalUrl.startsWith("http") ? design.originalUrl : `${API_BASE}${design.originalUrl}`)
    : ((design.editedUrl || design.originalUrl).startsWith("http")
        ? design.editedUrl || design.originalUrl
        : `${API_BASE}${design.editedUrl || design.originalUrl}`);

  async function handleSave() {
    setSaving(true);
    try {
      const tags = tagsStr.split(",").map((t) => t.trim()).filter(Boolean);
      await onUpdate({ name, sku: sku || undefined, tags });
      setEditMode(false);
    } finally {
      setSaving(false);
    }
  }

  const [downloading, setDownloading] = useState(false);
  // Kaynak analizin kisisellestirme alanlari (duzenlenebilir)
  const analysisId = design.sourceAnalysis?.id;
  const [perso, setPerso] = useState<PersonalizationField[]>([]);
  const [persoDirty, setPersoDirty] = useState(false);
  const [persoSaving, setPersoSaving] = useState(false);
  const [showOrderModal, setShowOrderModal] = useState(false);
  useEffect(() => {
    let cancelled = false;
    if (!analysisId) { setPerso([]); return; }
    apiClient.getAnalysis(analysisId).then((a) => {
      if (cancelled) return;
      const p = ((a as Analysis).source?.rawData?.personalization as PersonalizationField[]) || [];
      setPerso(p); setPersoDirty(false);
    }).catch(() => {});
    return () => { cancelled = true; };
  }, [analysisId]);
  async function savePerso() {
    if (!analysisId) return;
    setPersoSaving(true);
    try { await apiClient.setAnalysisPersonalization(analysisId, perso); setPersoDirty(false); }
    finally { setPersoSaving(false); }
  }
  // DB'de width/height yoksa gorselin dogal boyutundan goster
  const [imgDims, setImgDims] = useState<{ w: number; h: number } | null>(null);
  /** Baski dosyasi (DTF): kirpilmis + 300 DPI; standart 11 inc / big print 16 inc */
  async function handlePrintDownload(size: "standard" | "big", which: "main" | "variant" = "main") {
    setDownloading(true);
    try {
      await downloadPrintFile(design.id, { size, which, filename: design.sku || design.name });
    } catch (err) {
      alert(err instanceof Error ? err.message : String(err));
    } finally {
      setDownloading(false);
    }
  }

  async function handleDownload() {
    setDownloading(true);
    try {
      const url = absUrl(design.editedUrl || design.originalUrl);
      const res = await fetch(url);
      const blob = await res.blob();
      const ext = ((blob.type.split("/")[1] || "png").split("+")[0]).replace("jpeg", "jpg");
      const objectUrl = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = objectUrl;
      a.download = `${design.sku || design.name || "tasarim"}.${ext}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(objectUrl);
    } catch {
      window.open(absUrl(design.editedUrl || design.originalUrl), "_blank");
    } finally {
      setDownloading(false);
    }
  }

  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-6xl max-h-[90vh] overflow-hidden flex flex-col" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="flex items-center justify-between gap-3 p-4 border-b">
          <h2 className="min-w-0 truncate text-lg font-semibold">{design.name}</h2>
          {/* Liste Denetimi secim modu: tasarimi sec ve sonrakine gec */}
          {selectMode && (
            <div className="ml-auto flex shrink-0 items-center gap-2">
              {/* Secmeden gezinme */}
              <button
                onClick={selectMode.onPrev}
                disabled={!selectMode.hasPrev}
                title="Önceki tasarım"
                className="rounded-lg border border-gray-200 px-2 py-1.5 text-gray-600 hover:bg-gray-50 disabled:opacity-40"
              >
                <ChevronLeft className="h-4 w-4" />
              </button>
              <span className="text-[11px] text-gray-400">{selectMode.index + 1}/{selectMode.total}</span>
              <button
                onClick={selectMode.onNext}
                disabled={!selectMode.hasNext}
                title="Sonraki tasarım"
                className="rounded-lg border border-gray-200 px-2 py-1.5 text-gray-600 hover:bg-gray-50 disabled:opacity-40"
              >
                <ChevronRight className="h-4 w-4" />
              </button>
              <button
                onClick={selectMode.onToggle}
                className={`rounded-lg border px-3 py-1.5 text-xs font-medium ${
                  selectMode.selected
                    ? "border-indigo-500 bg-indigo-50 text-indigo-700"
                    : "border-gray-200 text-gray-600 hover:bg-gray-50"
                }`}
              >
                {selectMode.selected ? "Seçildi ✓" : "Seç"}
              </button>
              <button
                onClick={selectMode.onSelectAndNext}
                disabled={!selectMode.hasNext}
                className="rounded-lg bg-indigo-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-indigo-700 disabled:opacity-50"
              >
                Seç ve sonrakine geç →
              </button>
            </div>
          )}
          <button onClick={onClose} className="shrink-0 rounded-lg p-2 hover:bg-gray-100">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto flex flex-col md:flex-row">
          {/* Sol: gorsel + kisisellestirme (foto altinda) */}
          <div className="md:flex-1 flex flex-col min-w-0">
            <div
              ref={previewRef}
              className={`relative flex h-[68vh] flex-shrink-0 items-center justify-center overflow-hidden p-3 ${PREVIEW_BG_CLASS[previewBg]}`}
              onMouseLeave={() => setLoupe(null)}
            >
              {/* Zemin secici: seffaf alanlar beyaz zeminde fark edilmiyor. */}
              <div className="absolute left-2 top-2 z-20 flex gap-1 rounded-lg bg-white/85 p-0.5 shadow-sm">
                {(Object.keys(PREVIEW_BG_CLASS) as PreviewBg[]).map((k) => (
                  <button
                    key={k}
                    onClick={() => { setPreviewBg(k); try { localStorage.setItem("erc:preview-bg", k); } catch { /* ignore */ } }}
                    title={PREVIEW_BG_LABEL[k]}
                    className={`h-5 w-5 rounded border ${PREVIEW_BG_CLASS[k]} ${
                      previewBg === k ? "border-indigo-500 ring-2 ring-indigo-200" : "border-gray-300"
                    }`}
                  />
                ))}
              </div>

              {/* Yakinlastirma: 1x'te buyutec, ustunde kaydirarak inceleme */}
              <div className="absolute right-2 top-2 z-20 flex items-center gap-1 rounded-lg bg-white/85 p-0.5 text-xs shadow-sm">
                <button onClick={() => setZoom((z) => Math.max(1, Math.round((z - 0.5) * 10) / 10))}
                  className="px-1.5 py-0.5 text-gray-600 hover:text-indigo-600" title="Uzaklaştır">−</button>
                <span className="w-9 text-center tabular-nums text-gray-500">{Math.round(zoom * 100)}%</span>
                <button onClick={() => setZoom((z) => Math.min(6, Math.round((z + 0.5) * 10) / 10))}
                  className="px-1.5 py-0.5 text-gray-600 hover:text-indigo-600" title="Yakınlaştır">+</button>
                {zoom !== 1 && (
                  <button onClick={() => setZoom(1)} className="px-1.5 py-0.5 text-[11px] text-indigo-600">sıfırla</button>
                )}
              </div>

              <div className={zoom > 1 ? "h-full w-full overflow-auto" : "flex h-full w-full items-center justify-center"}>
                <img
                  src={displayUrl}
                  alt={design.name}
                  onLoad={(e) => { const el = e.currentTarget; if (el.naturalWidth) setImgDims({ w: el.naturalWidth, h: el.naturalHeight }); }}
                  title={zoom > 1 ? "Kaydırarak gez" : "Üzerine gelince büyüteç"}
                  onMouseMove={(e) => {
                    if (zoom !== 1) return;
                    const img = e.currentTarget.getBoundingClientRect();
                    const box = previewRef.current?.getBoundingClientRect();
                    if (!box) return;
                    const Z = 3;               // buyutec carpani
                    const S = 160;             // buyutec capi
                    const x = e.clientX - img.left;
                    const y = e.clientY - img.top;
                    setLoupe({
                      cx: img.left - box.left + x,
                      cy: img.top - box.top + y,
                      bgW: img.width * Z,
                      bgH: img.height * Z,
                      bgX: -(x * Z - S / 2),
                      bgY: -(y * Z - S / 2),
                    });
                  }}
                  style={zoom > 1 ? { width: `${zoom * 100}%`, maxWidth: "none" } : undefined}
                  className={zoom > 1 ? "h-auto object-contain" : "max-h-full max-w-full object-contain"}
                />
              </div>

              {/* Buyutec: imlecin altindaki alani 3x gosterir */}
              {zoom === 1 && loupe && (
                <div
                  className="pointer-events-none absolute z-10 rounded-full border-2 border-white shadow-2xl"
                  style={{
                    width: 160,
                    height: 160,
                    left: loupe.cx - 80,
                    top: loupe.cy - 80,
                    backgroundImage: `url(${displayUrl})`,
                    backgroundRepeat: "no-repeat",
                    backgroundSize: `${loupe.bgW}px ${loupe.bgH}px`,
                    backgroundPosition: `${loupe.bgX}px ${loupe.bgY}px`,
                  }}
                />
              )}

            </div>

            {/* Bilgiler gorselin ALTINDA — sag panel daralsin, tasarim buyusun */}
            <div className="flex flex-wrap items-center gap-x-4 gap-y-1 border-t px-4 py-2 text-xs">
              <span className="text-gray-500">SKU <span className="ml-1 font-mono text-gray-800">{design.sku || "-"}</span></span>
              <span className="text-gray-500">Kaynak <span className="ml-1 text-gray-800">{SOURCE_LABELS[design.source] || design.source}</span></span>
              <span className="text-gray-500">Boyut <span className="ml-1 text-gray-800">{(() => {
                const w = design.width ?? imgDims?.w;
                const h = design.height ?? imgDims?.h;
                return w && h ? `${w}x${h}` : "-";
              })()}</span></span>
              <span className="text-gray-500">{new Date(design.createdAt).toLocaleDateString("tr-TR")}</span>
              {design.copyright?.level && <CopyrightBadge copyright={design.copyright} />}
              {design.custom && <span className="rounded-full bg-pink-100 px-2 py-0.5 text-[11px] font-semibold text-pink-700">Custom</span>}
              {design.copyright?.summary && (
                <span className="w-full text-[11px] leading-relaxed text-gray-500">{design.copyright.summary}</span>
              )}
            </div>
            <DesignSeoBlock metadata={design.metadata} />
            {/* Kişiselleştirme alanları (fotoğrafın altında, düzenlenebilir) */}
            {analysisId && (perso.length > 0 || design.custom) && (
              <div className="border-t p-4">
                <div className="mb-2 flex items-center justify-between">
                  <span className="text-xs font-semibold text-gray-600">Kişiselleştirme ({perso.length})</span>
                  <div className="flex items-center gap-1.5">
                    {perso.length > 0 && (
                      <button onClick={() => setShowOrderModal(true)}
                        className="rounded bg-indigo-600 px-2.5 py-1 text-[11px] font-medium text-white hover:bg-indigo-700">
                        Sipariş Üret
                      </button>
                    )}
                    {persoDirty && (
                      <button onClick={savePerso} disabled={persoSaving}
                        className="rounded bg-pink-600 px-2.5 py-1 text-[11px] font-medium text-white hover:bg-pink-700 disabled:opacity-50">
                        {persoSaving ? "Kaydediliyor…" : "Kaydet"}
                      </button>
                    )}
                  </div>
                </div>
                <PersonalizationFieldsEditor fields={perso} onChange={(f) => { setPerso(f); setPersoDirty(true); }} />
              </div>
            )}
          </div>

          {/* Info panel */}
          <div className="md:w-72 p-4 space-y-4 border-l">
            {/* Original vs edited toggle */}
            {design.editedUrl && (
              <div className="flex gap-2">
                <button
                  onClick={() => showOriginal && onToggleOriginal()}
                  className={`flex-1 py-1.5 text-xs rounded-lg border ${!showOriginal ? "bg-indigo-50 border-indigo-300 text-indigo-700" : "text-gray-500"}`}
                >
                  Düzenlenmiş
                </button>
                <button
                  onClick={() => !showOriginal && onToggleOriginal()}
                  className={`flex-1 py-1.5 text-xs rounded-lg border ${showOriginal ? "bg-indigo-50 border-indigo-300 text-indigo-700" : "text-gray-500"}`}
                >
                  Orijinal
                </button>
              </div>
            )}

            {editMode ? (
              <div className="space-y-3">
                <div>
                  <label className="text-xs font-medium text-gray-600">İsim</label>
                  <input type="text" value={name} onChange={(e) => setName(e.target.value)} className="w-full border rounded-lg px-3 py-1.5 text-sm mt-1" />
                </div>
                <div>
                  <label className="text-xs font-medium text-gray-600">SKU</label>
                  <input type="text" value={sku} onChange={(e) => setSku(e.target.value)} className="w-full border rounded-lg px-3 py-1.5 text-sm mt-1" placeholder="örnek: TSH-001" />
                </div>
                <div>
                  <label className="text-xs font-medium text-gray-600">Etiketler (virgül ile)</label>
                  <input type="text" value={tagsStr} onChange={(e) => setTagsStr(e.target.value)} className="w-full border rounded-lg px-3 py-1.5 text-sm mt-1" />
                </div>
                <div className="flex gap-2">
                  <button onClick={handleSave} disabled={saving} className="flex-1 py-2 bg-indigo-600 text-white rounded-lg text-sm hover:bg-indigo-700 disabled:opacity-50">
                    {saving ? "Kaydediliyor..." : "Kaydet"}
                  </button>
                  <button onClick={() => setEditMode(false)} className="px-4 py-2 text-sm text-gray-600 border rounded-lg hover:bg-gray-50">
                    İptal
                  </button>
                </div>
              </div>
            ) : (
              <div className="space-y-3">
                <div>
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-gray-500">İlham alınan analiz</span>
                    <div className="flex items-center gap-2">
                      <button onClick={onLinkAnalysis} className="inline-flex items-center gap-1 text-[11px] font-medium text-indigo-600 hover:underline">
                        <Link2 className="h-3 w-3" /> {design.sourceAnalysis ? "Değiştir" : "Ekle"}
                      </button>
                      {design.sourceAnalysis && (
                        <button onClick={onRemoveAnalysis} className="inline-flex items-center gap-1 text-[11px] font-medium text-gray-400 hover:text-red-500">
                          <X className="h-3 w-3" /> Kaldır
                        </button>
                      )}
                    </div>
                  </div>
                  {design.sourceAnalysis ? (
                    <div className="mt-1 flex items-start gap-3 rounded-lg border border-gray-200 p-2">
                      {design.sourceAnalysis.imageUrl ? (
                        <a href={design.sourceAnalysis.imageUrl} target="_blank" rel="noopener noreferrer"
                          title="Görseli yeni sekmede aç" className="group relative flex-shrink-0">
                          <img src={design.sourceAnalysis.imageUrl} alt="" loading="lazy"
                            className="h-16 w-16 rounded-md border border-gray-100 object-cover" />
                        </a>
                      ) : (
                        <span className="flex h-16 w-16 flex-shrink-0 items-center justify-center rounded-md border border-gray-100 bg-gray-50"><ImageIcon className="h-5 w-5 text-gray-300" /></span>
                      )}
                      <div className="min-w-0 flex-1">
                        {design.sourceAnalysis.title && (
                          <p className="line-clamp-3 text-xs text-gray-600">{design.sourceAnalysis.title}</p>
                        )}
                        <Link to={`/analyses/${design.sourceAnalysis.id}`}
                          className="mt-1 inline-flex items-center gap-0.5 text-[11px] font-medium text-indigo-600 hover:underline">
                          Analizi Aç <ExternalLink className="h-3 w-3" />
                        </Link>

                        {/* Satan mockup: bu ilham listesinden uretilecek listenin
                            ana gorseli. Atanmamissa buradan secilebilir. */}
                        <div className="mt-2">
                          <SellingMockupPicker
                            analysisId={design.sourceAnalysis.id}
                            designId={design.id}
                            value={design.sellingMockup ?? design.sourceAnalysis.sellingMockup ?? null}
                            productTypeKey={design.sourceAnalysis.productTypeKey}
                            inspiration={design.sourceAnalysis.imageUrl
                              ? { imageUrl: design.sourceAnalysis.imageUrl, title: design.sourceAnalysis.title }
                              : null}
                            onChange={onSellingMockupChange}
                          />
                        </div>
                      </div>
                    </div>
                  ) : (
                    <p className="mt-1 rounded-lg border border-dashed border-gray-200 px-3 py-2 text-xs text-gray-400">
                      Bu tasarıma henüz bir ilham analizi bağlı değil. "Ekle" ile bir analiz seç.
                    </p>
                  )}
                </div>
                {design.tags.length > 0 && (
                  <div>
                    <span className="text-xs text-gray-500">Etiketler</span>
                    <div className="flex flex-wrap gap-1 mt-1">
                      {design.tags.map((tag) => (
                        <span key={tag} className="text-xs px-2 py-0.5 bg-gray-100 text-gray-600 rounded">
                          {tag}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
                {design.listings && design.listings.length > 0 && (
                  <div>
                    <span className="text-xs text-gray-500">Bağlı Listeler ({design.listings.length})</span>
                    <div className="mt-1 space-y-2">
                      {design.listings.map((l) => (
                        <div key={l.id} className="rounded-lg border border-gray-200 p-2.5 text-sm">
                          <Link to={`/listing-builder?id=${l.listingDraftId}`} className="font-medium text-indigo-600 hover:underline line-clamp-1">
                            {l.listingDraft?.title || "İsimsiz liste"}
                          </Link>
                          <div className="mt-1 flex flex-wrap gap-1.5 text-[11px]">
                            {(() => {
                              const onEtsy = !!l.listingDraft?.etsyListingId;
                              const st = l.listingDraft?.status;
                              if (onEtsy && st === "published") return <span className="rounded bg-emerald-50 px-1.5 py-0.5 font-medium text-emerald-600">Etsy'de yayında</span>;
                              if (onEtsy && st === "draft") return <span className="rounded bg-amber-50 px-1.5 py-0.5 font-medium text-amber-600">Etsy'de taslak</span>;
                              if (onEtsy) return <span className="rounded bg-emerald-50 px-1.5 py-0.5 font-medium text-emerald-600">Etsy'de</span>;
                              return <span className="rounded bg-gray-100 px-1.5 py-0.5 text-gray-600">Yerel taslak</span>;
                            })()}
                            {l.listingTemplateName && (
                              <span className="rounded bg-blue-50 px-1.5 py-0.5 text-blue-600" title="Kullanılan liste şablonu">Şablon: {l.listingTemplateName}</span>
                            )}
                          </div>
                          {l.listingDraft?.mockupTemplates && l.listingDraft.mockupTemplates.length > 0 && (
                            <p className="mt-1 text-[11px] text-gray-500" title="Kullanılan mockup grubu">
                              Mockup Grubu: {l.listingDraft.mockupTemplates.join(", ")}
                            </p>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                <div>
                  <span className="text-xs text-gray-500">Oluşturulma</span>
                  <p className="text-sm">{new Date(design.createdAt).toLocaleDateString("tr-TR")}</p>
                </div>

                <PrintCheckPanel imageUrl={design.editedUrl || design.originalUrl}
                  productTypeKey={design.sellingMockup?.productTypeKey ?? design.sourceAnalysis?.productTypeKey ?? design.sourceAnalysis?.sellingMockup?.productTypeKey} />

                <div className="flex flex-col gap-2 pt-2 border-t">
                  {isApparelShop ? (<>
                  <button onClick={() => void handlePrintDownload("standard")} disabled={downloading}
                    title="Tedarikçiye gidecek dosya: boş kenarlar kırpılmış, 300 DPI, en fazla 11×16 inç"
                    className="w-full py-2 text-sm bg-gray-900 text-white rounded-lg hover:bg-gray-800 flex items-center justify-center gap-2 disabled:opacity-50">
                    {downloading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
                    {downloading ? "Hazırlanıyor..." : "Baskı Dosyasını İndir (11″)"}
                  </button>
                  <div className="flex gap-2 text-xs">
                    <button onClick={() => void handlePrintDownload("big")} disabled={downloading}
                      title="Big print: boş kenarlar kırpılmış, 300 DPI, Rexven Tam Boy kutusu 13×17,5 inç"
                      className="flex-1 rounded-lg border border-orange-200 py-1.5 text-orange-700 hover:bg-orange-50 disabled:opacity-50">
                      Big print (13×17,5″)
                    </button>
                    <button onClick={handleDownload} disabled={downloading}
                      title="Üretilen dosya olduğu gibi (mockup tuvali, kırpılmamış)"
                      className="flex-1 rounded-lg border border-gray-200 py-1.5 text-gray-600 hover:bg-gray-50 disabled:opacity-50">
                      Orijinal dosya
                    </button>
                  </div>
                  </>) : (
                    <button onClick={handleDownload} disabled={downloading}
                      title="Baskı dosyası: ürünün baskı alanına göre hazırlanmış kare dosya (kırpılmaz)"
                      className="w-full py-2 text-sm bg-gray-900 text-white rounded-lg hover:bg-gray-800 flex items-center justify-center gap-2 disabled:opacity-50">
                      {downloading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
                      {downloading ? "Hazırlanıyor..." : "Baskı Dosyasını İndir"}
                    </button>
                  )}
                  {!isApparelShop && (
                    <div className="flex gap-2">
                      <select value={fitShape} onChange={(e) => setFitShape(e.target.value as "round" | "heart" | "square")}
                        aria-label="Baskı alanı şekli" className="rounded-lg border border-gray-300 px-2 py-1.5 text-xs">
                        <option value="round">Yuvarlak</option>
                        <option value="heart">Kalp</option>
                        <option value="square">Kare</option>
                      </select>
                      <button onClick={() => void fitToPrintArea()} disabled={fitBusy}
                        title="Tasarım mockup'taki şeklin kendisine en büyük boyutta oturur (ince kenar payı); arka plan silinir, saf beyaz #FAFAFA olur. Orijinale Dön ile geri alınır."
                        className="flex-1 rounded-lg border border-indigo-300 py-1.5 text-xs font-medium text-indigo-700 hover:bg-indigo-50 disabled:opacity-50">
                        {fitBusy ? "Sığdırılıyor…" : "Baskı alanına sığdır"}
                      </button>
                    </div>
                  )}
                  {fitErr && <p className="text-xs text-red-600">{fitErr}</p>}
                  <button onClick={onOpenEditor} className="w-full py-2 text-sm border border-violet-300 text-violet-700 rounded-lg hover:bg-violet-50 flex items-center justify-center gap-2">
                    <Palette className="w-4 h-4" /> Editörde Düzenle
                  </button>
                  <button onClick={() => setEditMode(true)} className="w-full py-2 text-sm border rounded-lg hover:bg-gray-50 flex items-center justify-center gap-2">
                    <Edit3 className="w-4 h-4" /> Bilgileri Düzenle
                  </button>
                  <input ref={replaceInputRef} type="file" accept="image/*" className="hidden" onChange={onReplaceFile} />
                  <button onClick={() => replaceInputRef.current?.click()} disabled={replaceBusy}
                    className="w-full py-2 text-sm border rounded-lg hover:bg-gray-50 flex items-center justify-center gap-2 disabled:opacity-50">
                    {replaceBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <ImageIcon className="w-4 h-4" />}
                    {replaceBusy ? "Değiştiriliyor..." : "Tasarımı Değiştir"}
                  </button>
                  <Link
                    to={mockupHref}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-full py-2 text-sm rounded-lg bg-emerald-600 text-white hover:bg-emerald-700 flex items-center justify-center gap-2"
                  >
                    <ExternalLink className="w-4 h-4" /> Mockup Studio'da Aç
                  </Link>
                  <button onClick={openInCanva} disabled={canvaBusy}
                    className="w-full py-2 text-sm rounded-lg text-white flex items-center justify-center gap-2 disabled:opacity-50"
                    style={{ backgroundColor: "#7B2FF2" }}>
                    {canvaBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <ExternalLink className="w-4 h-4" />} Canva'da Aç
                  </button>
                  {canvaDesignId && (
                    <button onClick={syncFromCanva} disabled={syncBusy}
                      className="w-full py-2 text-sm rounded-lg border border-purple-300 text-purple-700 hover:bg-purple-50 flex items-center justify-center gap-2 disabled:opacity-50">
                      {syncBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <RotateCcw className="w-4 h-4" />} Canva'dan Al (Güncelle)
                    </button>
                  )}
                  {design.editedUrl && (
                    <button onClick={onRevert} className="w-full py-2 text-sm border border-amber-300 text-amber-700 rounded-lg hover:bg-amber-50 flex items-center justify-center gap-2">
                      <RotateCcw className="w-4 h-4" /> Orijinale Dön
                    </button>
                  )}

                  {/* Açık/Koyu tema varyasyonu (yalniz tisort) */}
                  {isApparelShop && <div className="rounded-lg border border-gray-200 p-3">
                    <div className="mb-2 flex items-center justify-between">
                      <span className="text-xs font-semibold text-gray-600">Tişört Varyasyonu</span>
                      {themeBusy && <Loader2 className="h-3.5 w-3.5 animate-spin text-gray-400" />}
                    </div>
                    <p className="mb-2 text-[10px] text-gray-400">Bu tasarım hangi tişört için? İşaretle, diğer varyasyonu yükle. İkisi tek tasarım olarak durur.</p>
                    <div className="mb-2 flex gap-2">
                      <button onClick={() => setTheme("light")} disabled={themeBusy}
                        title="Açık (light) tasarım — Koyu tişörtlerde kullanılır"
                        className={`flex flex-1 items-center justify-center gap-1 rounded-lg border py-1.5 text-xs ${theme === "light" ? "border-gray-700 bg-gray-800 text-white" : "text-gray-500 hover:bg-gray-50"}`}>
                        <Moon className="h-3.5 w-3.5" /> Koyu Tshirt
                      </button>
                      <button onClick={() => setTheme("dark")} disabled={themeBusy}
                        title="Koyu (dark) tasarım — Açık tişörtlerde kullanılır"
                        className={`flex flex-1 items-center justify-center gap-1 rounded-lg border py-1.5 text-xs ${theme === "dark" ? "border-amber-300 bg-amber-50 text-amber-700" : "text-gray-500 hover:bg-gray-50"}`}>
                        <Sun className="h-3.5 w-3.5" /> Açık Tshirt
                      </button>
                    </div>
                    <input ref={variantInputRef} type="file" accept="image/*" onChange={onVariantFile} className="hidden" />
                    {variantUrl ? (
                      <div className="flex items-center gap-2">
                        <img src={absUrl(variantUrl)} alt="" className="h-12 w-12 rounded border border-gray-200 object-contain bg-gray-50" />
                        <div className="flex-1 text-[11px] text-gray-500">Diğer varyasyon{variantTheme ? ` (${variantTheme === "dark" ? "Açık Tshirt" : "Koyu Tshirt"})` : ""} yüklendi</div>
                        <button onClick={() => variantInputRef.current?.click()} disabled={themeBusy} className="text-[11px] text-indigo-600 hover:text-indigo-700">Değiştir</button>
                        <button onClick={removeVariant} disabled={themeBusy} className="text-[11px] text-red-500 hover:text-red-700">Kaldır</button>
                      </div>
                    ) : null}
                    {variantUrl ? (
                      // Diger varyasyonun baski dosyasi: ana tasarimla ayni kirpma/olcu
                      <div className="flex gap-2 text-xs">
                        <button onClick={() => void handlePrintDownload("standard", "variant")} disabled={downloading}
                          title="Diğer varyasyonun baskı dosyası: boş kenarlar kırpılmış, 300 DPI, en fazla 11×16 inç"
                          className="flex flex-1 items-center justify-center gap-1 rounded-lg bg-gray-900 py-1.5 text-white hover:bg-gray-800 disabled:opacity-50">
                          {downloading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Download className="h-3.5 w-3.5" />} Baskı dosyası (11″)
                        </button>
                        <button onClick={() => void handlePrintDownload("big", "variant")} disabled={downloading}
                          title="Diğer varyasyonun big print dosyası: Rexven Tam Boy kutusu 13×17,5 inç"
                          className="flex-1 rounded-lg border border-orange-200 py-1.5 text-orange-700 hover:bg-orange-50 disabled:opacity-50">
                          Big print (13×17,5″)
                        </button>
                      </div>
                    ) : (
                      <button onClick={() => variantInputRef.current?.click()} disabled={themeBusy}
                        className="flex w-full items-center justify-center gap-1.5 rounded-lg border-2 border-dashed border-gray-200 py-2 text-xs text-gray-500 hover:border-indigo-300 hover:bg-indigo-50">
                        <Upload className="h-3.5 w-3.5" /> Diğer Varyasyonu Yükle
                      </button>
                    )}
                  </div>}

                  <button onClick={onDelete} className="w-full py-2 text-sm border border-red-300 text-red-600 rounded-lg hover:bg-red-50 flex items-center justify-center gap-2">
                    <Trash2 className="w-4 h-4" /> Sil
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {showOrderModal && analysisId && (
        <OrderDesignModal
          analysisId={analysisId}
          referenceImageUrl={design.editedUrl || design.originalUrl}
          fields={perso}
          productTypeKey={design.sourceAnalysis?.productTypeKey}
          onClose={() => setShowOrderModal(false)}
        />
      )}
    </div>
  );
}

// ─── Ilham Alinan Analiz Secici ─────────────────────────────
function AnalysisPickerModal({
  currentId,
  onClose,
  onPick,
}: {
  currentId?: string;
  onClose: () => void;
  onPick: (analysisId: string) => void | Promise<void>;
}) {
  const [analyses, setAnalyses] = useState<Analysis[]>([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [busyId, setBusyId] = useState<string | null>(null);

  useEffect(() => {
    apiClient.listAnalyses()
      .then((d) => setAnalyses(d as Analysis[]))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const filtered = analyses.filter((a) => {
    const s = q.trim().toLowerCase();
    if (!s) return true;
    return [a.source?.title, a.productType, a.niche, ...(a.source?.tags ?? [])]
      .filter(Boolean).some((v) => String(v).toLowerCase().includes(s));
  });

  async function pick(id: string) {
    setBusyId(id);
    try { await onPick(id); } finally { setBusyId(null); }
  }

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50 p-4" onClick={onClose}>
      <div className="flex max-h-[85vh] w-full max-w-2xl flex-col overflow-hidden rounded-xl bg-white shadow-xl" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between border-b border-gray-100 p-4">
          <h3 className="flex items-center gap-2 text-lg font-semibold text-gray-900"><Link2 className="h-5 w-5 text-indigo-600" /> İlham Analizi Seç</h3>
          <button onClick={onClose} className="rounded p-1 text-gray-400 hover:bg-gray-100"><X className="h-4 w-4" /></button>
        </div>
        <div className="border-b border-gray-100 p-3">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
            <input autoFocus value={q} onChange={(e) => setQ(e.target.value)} placeholder="Başlık, ürün tipi veya etiket ara…"
              className="w-full rounded-lg border border-gray-200 py-2 pl-9 pr-3 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-200" />
          </div>
        </div>
        <div className="flex-1 overflow-y-auto p-3">
          {loading ? (
            <div className="flex items-center justify-center gap-2 py-10 text-sm text-gray-400"><Loader2 className="h-4 w-4 animate-spin" /> Yükleniyor…</div>
          ) : filtered.length === 0 ? (
            <p className="py-10 text-center text-sm text-gray-400">Analiz bulunamadı.</p>
          ) : (
            <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
              {filtered.map((a) => {
                const thumb = a.source?.imageUrls?.[0];
                const isCur = a.id === currentId;
                return (
                  <button key={a.id} onClick={() => pick(a.id)} disabled={!!busyId}
                    className={`group flex flex-col overflow-hidden rounded-lg border text-left transition-colors disabled:opacity-60 ${isCur ? "border-indigo-500 ring-2 ring-indigo-200" : "border-gray-200 hover:border-indigo-300 hover:bg-indigo-50/40"}`}>
                    <div className="relative h-24 w-full bg-gray-100">
                      {thumb ? <img src={thumb} alt="" loading="lazy" className="h-full w-full object-cover" />
                        : <span className="flex h-full w-full items-center justify-center"><ImageIcon className="h-6 w-6 text-gray-300" /></span>}
                      {busyId === a.id && <span className="absolute inset-0 flex items-center justify-center bg-white/60"><Loader2 className="h-5 w-5 animate-spin text-indigo-600" /></span>}
                      {isCur && <span className="absolute right-1 top-1 rounded-full bg-indigo-600 px-1.5 py-0.5 text-[9px] font-semibold text-white">Seçili</span>}
                    </div>
                    <div className="p-2">
                      <p className="line-clamp-2 text-[11px] font-medium text-gray-700">{a.source?.title ?? a.productType}</p>
                      <p className="mt-0.5 text-[10px] text-gray-400">{a.productType}</p>
                    </div>
                  </button>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
